import { api, unwrap } from '@/lib/api'
import { orgWorkspacesQuery } from '@/api/org'
import { registerPaletteProvider, type PaletteResult } from './CommandPalette'

/**
 * Search providers for the Ctrl/⌘ K palette. Each one only asks the server for
 * what the person may read, and answers "nothing" (never an error) otherwise.
 */

const has = (perms: readonly string[], p: string) => perms.includes(p)

registerPaletteProvider({
  name: 'Workspaces',
  search: async (q, { session, queryClient }) => {
    const needle = q.toLowerCase()
    const openable = new Set(session.workspaces.map(w => w.id))
    const perms = session.permissions as string[]
    if (session.org && has(perms, 'workspace.read')) {
      const list = await queryClient.fetchQuery({ ...orgWorkspacesQuery(session.org.id, 'active'), staleTime: 60_000 })
      return list.items
        .filter(w => w.name.toLowerCase().includes(needle) || w.description.toLowerCase().includes(needle))
        .map<PaletteResult>(w =>
          openable.has(w.id)
            ? { id: w.id, label: w.name, hint: 'Workspace', href: `/w/${w.id}/overview` }
            : // Admins see every workspace but can only open the ones they belong to.
              { id: w.id, label: w.name, hint: 'Not a member · manage', href: '/org/workspaces' },
        )
    }
    return session.workspaces
      .filter(w => w.name.toLowerCase().includes(needle))
      .map(w => ({ id: w.id, label: w.name, hint: 'Workspace', href: `/w/${w.id}/overview` }))
  },
})

registerPaletteProvider({
  name: 'Memories',
  heading: ({ workspaceId, session }) => {
    const w = session.workspaces.find(x => x.id === workspaceId)
    return w ? `Memories in ${w.name}` : 'Memories'
  },
  search: async (q, { workspaceId, session }) => {
    if (!workspaceId || !session.workspaces.some(w => w.id === workspaceId)) return []
    if (!has(session.permissions as string[], 'memory.read')) return []
    const r = await unwrap(api.GET('/esm/v1/workspaces/{workspace_id}/memories', { params: { path: { workspace_id: workspaceId }, query: { q, limit: 6 } } }))
    return r.items
      .filter(m => m.row_kind === 'memory')
      .map(m => ({
        id: m.memory_id,
        label: m.title || m.snippet,
        hint: m.status === 'active' ? m.scope : `${m.scope} · ${m.status.replace(/_/g, ' ')}`,
        href: `/w/${workspaceId}/memories/${m.memory_id}`,
      }))
  },
})
